import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { actionLinkLifetime } from "@rago/web-shared";
import { z } from "zod";
import { api } from "../../lib/apiClient";
import { auth } from "../../lib/authManager";
import { useAuth } from "../../lib/useAuth";
import { describeAuthError } from "../../lib/authForm";
import { ErrorNote, SuccessNote, Spinner } from "../../components/ui";
import { AuthBody, AuthHeading, AuthPending } from "../../components/auth-card";

export const Route = createFileRoute("/auth/verify")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: Verify,
  pendingComponent: AuthPending,
});

/** The same number the API stamps on the token and prints in the mail. */
const LINK_LIFE = actionLinkLifetime("verify_email");

function Verify() {
  const { token } = Route.useSearch();
  const session = useAuth();
  const [state, setState] = useState<"working" | "done" | "failed" | "missing">(
    token ? "working" : "missing",
  );
  const [error, setError] = useState<unknown>(null);
  const [resend, setResend] = useState<"idle" | "busy" | "sent">("idle");
  const [resendError, setResendError] = useState<unknown>(null);

  const fired = useRef(false);
  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    api("/v1/auth/verify-email", { body: { token }, auth: false })
      .then(async () => {
        setState("done");
        // Pull the fresh user so the "verify your email" banner clears.
        if (auth.getState().status === "signedIn") {
          await auth.reloadMe().catch(() => {});
        }
      })
      .catch((err) => {
        setError(err);
        setState("failed");
      });
  }, [token]);

  async function sendNewLink() {
    setResend("busy");
    setResendError(null);
    try {
      await api("/v1/auth/resend-verification", { method: "POST", body: {} });
      setResend("sent");
    } catch (err) {
      setResendError(err);
      setResend("idle");
    }
  }

  // The failed state's way out depends on who is here: the resend endpoint
  // needs a session, so a signed-out reader goes through sign-in and comes
  // back to this same link.
  function failedActions() {
    if (session.status === "loading") return <Spinner />;
    if (session.status === "signedIn") {
      if (session.user.emailVerified) {
        return (
          <>
            <p className="mb-4 text-sm text-[var(--rago-text-2)]">Your email is already verified.</p>
            <Link to="/" className="rago-btn rago-btn-block rago-btn-lg">
              Continue to Rago
            </Link>
          </>
        );
      }
      return (
        <>
          <p className="mb-4 text-sm text-[var(--rago-text-2)]">
            Verification links work for {LINK_LIFE}. We can send a fresh one to {session.user.email}.
          </p>
          <ErrorNote error={resendError}>
            {resendError ? describeAuthError(resendError, "resend").message : null}
          </ErrorNote>
          {resend === "sent" ? (
            <SuccessNote>
              New link sent to {session.user.email}. It works for {LINK_LIFE}.
            </SuccessNote>
          ) : (
            <button
              type="button"
              className="rago-btn rago-btn-block rago-btn-lg"
              disabled={resend === "busy"}
              onClick={() => void sendNewLink()}
            >
              {resend === "busy" ? <Spinner /> : null}
              Send a new link
            </button>
          )}
        </>
      );
    }
    const back = `/auth/verify?token=${encodeURIComponent(token ?? "")}`;
    return (
      <>
        <p className="mb-4 text-sm text-[var(--rago-text-2)]">
          Verification links work for {LINK_LIFE}. Sign in and we'll send a fresh one.
        </p>
        <Link to="/auth/signin" search={{ next: back }} className="rago-btn rago-btn-block rago-btn-lg">
          Sign in to send a new link
        </Link>
      </>
    );
  }

  return (
    <AuthBody>
      <AuthHeading title="Email verification" />
      <div className="text-center">
        {state === "working" ? (
          <p className="flex items-center justify-center gap-2 text-sm text-[var(--rago-text-2)]">
            <Spinner /> Confirming your email…
          </p>
        ) : state === "done" ? (
          <>
            <SuccessNote>Your email is verified. You're all set.</SuccessNote>
            <Link to="/" className="rago-btn rago-btn-block rago-btn-lg">
              Continue to Rago
            </Link>
          </>
        ) : state === "missing" ? (
          <p className="text-sm text-[var(--rago-text-2)]">
            This page needs the link from your verification email.
          </p>
        ) : (
          <>
            <ErrorNote error={error}>{describeAuthError(error, "verify").message}</ErrorNote>
            {failedActions()}
            <div className="mt-5 text-center text-sm">
              <Link to="/" className="rago-link">
                Continue without verifying
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthBody>
  );
}
